import React from "react";
import {Button} from "@/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown";
import {CheckCheck, ExternalLink, ListChecks} from "lucide-react";
import {useSave} from "@/contexts/SaveContext";
import {FlowNodesData} from "../NodeTypes/NodesData";
import {useSelectedElements} from "@/hooks/useSelectedElements";
import {Icon} from "@/components/ui/icon";
import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/ui/tooltip";
import {useReactFlow} from "@xyflow/react";
import {FlowNodeData, FlowNodeTypes} from "@/types/node";
import {Node} from "@xyflow/react";
import {useSubflows} from "@/contexts/SubflowsContext";

const FIT_VIEW_OFFSET = 600;

const Checklist = () => {
  const {flowIssues} = useSave();
  const {setSelectedNode} = useSelectedElements();
  const {getNode, setViewport} = useReactFlow();
  const {selectSubflow, selectMainFlow} = useSubflows();
  const hasIssues = flowIssues.length > 0;

  const fitToNode = (node: Node<FlowNodeData>) => {
    if (node) {
      const iframeDoc = window.__flowBuilderIframeDoc;
      const {x, y} = node.position;
      const {width, height} = node.measured;
      const zoom = 1.5;

      setViewport(
        {
          x:
            -x * zoom +
            iframeDoc.body.offsetWidth / 2 +
            width / 2 -
            FIT_VIEW_OFFSET,
          y: -y * zoom + iframeDoc.body.offsetHeight / 2 - height,
          zoom: zoom,
        },
        {duration: 1000},
      );
    }
  };

  return (
    <DropdownMenu>
      <Tooltip delayDuration={0} disableHoverableContent={true}>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="[&_svg]:size-5 px-2 relative"
            >
              <ListChecks />
              {hasIssues && (
                <span className="text-xs absolute top-0 -right-1.5 w-5 h-5 rounded-full bg-red bg-opacity-12 text-red flex items-center justify-center">
                  {flowIssues.length}
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom" sideOffset={10}>
          Checklist
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent
        align="end"
        className="rounded-xl p-4 w-80 bg-white text-primary border border-border flex flex-col"
      >
        <div className="mb-3">
          <h4 className="text-base text-primary font-semibold">Checklist</h4>
          <p className="text-sm text-secondary">
            Resolve all issues before publishing.
          </p>
        </div>
        {hasIssues ? (
          <div className="flex flex-col gap-2 max-h-160 overflow-y-auto">
            {flowIssues.map(issue => {
              const nodeConfig = FlowNodesData[issue.nodeType];
              const nodeIcon = nodeConfig?.icon;
              const notNodeIssue = issue.nodeId === null;

              const nodeStyles = {
                icon: {
                  color: nodeConfig.styles.inverted
                    ? "white"
                    : nodeConfig.styles.iconColor,
                  backgroundColor: nodeConfig.styles.backgroundColor,
                  borderColor: nodeConfig.styles.iconBorderColor,
                },
              };

              return (
                <DropdownMenuItem
                  key={issue.nodeId || issue.key}
                  className="p-2 flex flex-col hover:bg-slate-50 border border-border rounded-lg items-start"
                  onClick={async () => {
                    if (issue.nodeId === null) return;

                    // Switch to the correct subflow if needed
                    if (issue.subflowId) {
                      if (issue.subflowId === "main") {
                        selectMainFlow();
                      } else {
                        selectSubflow(issue.subflowId);
                      }

                      // Wait for subflow to switch and nodes to load
                      await new Promise(resolve => setTimeout(resolve, 100));
                    }

                    // Get the node and fit to it
                    const node = getNode(issue.nodeId);
                    if (node) {
                      fitToNode(node as Node<FlowNodeData>);

                      if (
                        !(
                          issue.nodeType === FlowNodeTypes.ACTION ||
                          issue.nodeType === FlowNodeTypes.TRANSFER ||
                          issue.nodeType === FlowNodeTypes.BOOKING
                        )
                      ) {
                        setSelectedNode(issue.nodeId);
                      }
                    }
                  }}
                >
                  <div className="flex gap-2 w-full justify-between">
                    <div
                      style={nodeStyles.icon}
                      className="mr-auto py-1 px-2  flex gap-2 items-center rounded border-0"
                    >
                      {nodeIcon && React.createElement(nodeIcon)}
                      {issue.nodeTitle}
                    </div>
                    {!notNodeIssue && (
                      <ExternalLink
                        className="size-4 text-disabled"
                        strokeWidth={1.5}
                      />
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    {issue.subflowName && (
                      <span className="text-xs text-secondary">
                        {issue.subflowName}
                      </span>
                    )}
                    <span>{issue.message}</span>
                  </div>
                </DropdownMenuItem>
              );
            })}
          </div>
        ) : (
          <div className="p-3 flex flex-col border border-border rounded-lg items-center">
            <Icon className="size-14 bg-green bg-opacity-8 rounded-lg border border-green border-opacity-16 mb-4">
              <CheckCheck className="text-green size-8" />
            </Icon>
            <p className="text-base font-medium mb-0.5">There's no issues</p>
            <p className="text-sm text-secondary">
              Your flow is ready to be published
            </p>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default Checklist;
